/**
 * One amount formatter for both of SkyVN's monies.
 *
 * Background — a Vietnamese booking carries **two** separate sums and the CRM now shows
 * both on the same screen, so they must be formatted by the same code or they will drift apart
 * in front of the user:
 *
 *  - the **booking fee** (the reservation token) — `unit_resv_tokens` / `ur_token_payments`,
 *    carried in `amount_minor`, which for VND is already the whole đồng (no sub-unit);
 *  - the **deposit** — `booking_fee_payments`, carried in `amount_cents`, which mhub-book-api
 *    multiplies by 100 even for VND, so it has to be divided back before display.
 *
 * `formatCurrencyAmount` was the local `formatAmount` inside
 * `pages/Opportunity/BookingDetail/PaymentSection.jsx`; it is moved here unchanged so the deposit
 * panel cannot invent a second way of writing an amount, and so both can be unit-tested without
 * importing the app root.
 */

/** mhub-book-api stores a deposit in `amount_cents`, ×100 even for VND. */
export const CENTS_PER_UNIT = 100;

/**
 * An amount already expressed in whole currency units, with its currency code in front.
 *
 * @param {number|string|null|undefined} amount  whole currency units (đồng, ringgit, …)
 * @param {string} [currencyCode]
 * @returns {string} e.g. "VND 50,000,000.00", or "-" when there is no amount
 */
export function formatCurrencyAmount(amount, currencyCode) {
  if (amount == null) return '-';
  return `${currencyCode || ''} ${Number(amount).toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}`.trim();
}

/**
 * Whole currency units from a `booking_fee_payments.amount_cents` value.
 *
 * Returns null rather than 0 for a missing value, so "no amount" stays distinguishable from
 * "an amount of zero" — a deposit of 0 is a real state on a rejected payment.
 *
 * @param {number|string|null|undefined} cents
 * @returns {number|null}
 */
export function centsToUnits(cents) {
  if (cents == null || cents === '') return null;
  const n = Number(cents);
  if (Number.isNaN(n)) return null;
  return n / CENTS_PER_UNIT;
}

/**
 * A deposit amount held in `amount_cents`, formatted for display.
 *
 * Prefer the server's own `amount_text` where the API supplies one: mhub-book-api builds it with
 * the same `system.FormatAmountVN` the printed deposit receipt uses, so taking it verbatim is the
 * only way the screen and the receipt cannot disagree. This is the fallback for
 * when it is absent.
 *
 * @param {number|string|null|undefined} cents
 * @param {string} [currencyCode]
 * @returns {string}
 */
export function formatCentsAmount(cents, currencyCode) {
  return formatCurrencyAmount(centsToUnits(cents), currencyCode);
}
